import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Users, Phone, Mail, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
  const [customers, setCustomers] = useState(null);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", zip_code: "", notes: "" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    const p = search ? `?search=${encodeURIComponent(search)}` : "";
    api.get(`/customers${p}`).then(({ data }) => setCustomers(data));
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setLoading(true);
    try {
      await api.post("/customers", form);
      toast.success("Customer created");
      setDialogOpen(false);
      setForm({ name: "", phone: "", email: "", address: "", zip_code: "", notes: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/customers/${id}`);
    setDetail(data);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading">Customers</h1>
          <p className="text-slate-500 mt-1">Your customer database and job history.</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-customer-btn" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> New customer</Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers..." data-testid="customer-search" className="pl-9" />
      </div>

      {customers === null ? <Skeleton className="h-80 rounded-xl" /> : customers.length === 0 ? (
        <Card className="p-12 text-center border-slate-200" data-testid="customers-empty">
          <Users className="h-12 w-12 text-slate-300 mx-auto" />
          <h3 className="mt-4 font-heading font-semibold text-lg">No customers yet</h3>
          <p className="text-slate-500 mt-1">Convert a lead or add a customer directly.</p>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead className="hidden sm:table-cell">Phone</TableHead><TableHead className="hidden md:table-cell">Email</TableHead><TableHead className="hidden lg:table-cell">ZIP</TableHead></TableRow></TableHeader>
              <TableBody>
                {customers.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(c.id)} data-testid={`customer-row-${c.id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="hidden sm:table-cell text-slate-600">{c.phone || "—"}</TableCell>
                    <TableCell className="hidden md:table-cell text-slate-600">{c.email || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell text-slate-600">{c.zip_code || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="customer-dialog">
          <DialogHeader><DialogTitle>New customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={set("name")} data-testid="customer-name" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="customer-phone" className="mt-1" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={set("email")} data-testid="customer-email" className="mt-1" /></div>
            </div>
            <div><Label>Address</Label><Input value={form.address} onChange={set("address")} data-testid="customer-address" className="mt-1" /></div>
            <div><Label>ZIP</Label><Input value={form.zip_code} onChange={set("zip_code")} data-testid="customer-zip" className="mt-1 max-w-[160px]" /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} data-testid="customer-notes" className="mt-1" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={loading} data-testid="customer-save-btn" className="bg-blue-600 hover:bg-blue-700">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="customer-detail-sheet">
          {detail && (
            <>
              <SheetHeader><SheetTitle>{detail.customer.name}</SheetTitle></SheetHeader>
              <div className="mt-6 space-y-5 text-sm">
                <div className="space-y-2">
                  {detail.customer.phone && <div className="flex items-center gap-2 text-slate-600"><Phone className="h-4 w-4" />{detail.customer.phone}</div>}
                  {detail.customer.email && <div className="flex items-center gap-2 text-slate-600"><Mail className="h-4 w-4" />{detail.customer.email}</div>}
                  {detail.customer.address && <div className="flex items-center gap-2 text-slate-600"><MapPin className="h-4 w-4" />{detail.customer.address}</div>}
                </div>
                {detail.customer.notes && <div><h4 className="font-semibold mb-1">Notes</h4><p className="text-slate-600">{detail.customer.notes}</p></div>}
                <div>
                  <h4 className="font-semibold mb-2">Leads ({detail.leads.length})</h4>
                  {detail.leads.length === 0 ? <p className="text-slate-400">No linked leads.</p> : detail.leads.map((l) => (
                    <div key={l.id} className="flex items-center justify-between py-1.5 border-b last:border-0"><span>{l.service || "Service request"}</span><Badge variant="secondary">{l.status}</Badge></div>
                  ))}
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Appointments ({detail.appointments.length})</h4>
                  {detail.appointments.length === 0 ? <p className="text-slate-400">No appointments.</p> : detail.appointments.map((a) => (
                    <div key={a.id} className="flex items-center justify-between py-1.5 border-b last:border-0"><span>{a.title}</span><span className="text-slate-500 text-xs">{new Date(a.start).toLocaleDateString()}</span></div>
                  ))}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
